import getPillarLocalDevelopment from '@jira/ssr__global-build-context/src/getPillarLocalDevelopment.tsx';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
// eslint-disable-next-line jira/import-whitelist
import { getCookie } from '@jira/platform__browser-storage-controls/src/utils/get-cookie/index.tsx';
// eslint-disable-next-line jira/import-whitelist
import {
	ATLASSIAN_CONSENT_COOKIE_KEY,
	ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY,
	ATLASSIAN_SHOW_BANNER_COOKIE_KEY,
} from '@atlassian/browser-storage-controls/constants';
import type { ConsentHubResponse } from '@atlassian/browser-storage-controls/consent-hub-service/types';
import type { PrefetchedConsentPreferenceData } from '@atlassian/browser-storage-controls/controllers/banner-controls/types';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	createResource,
	useResource,
	type RouteResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';
import { getAtlassianAccountId } from '@atlassian/jira-common-util-get-tenant-context';
import {
	CONSENT_BANNER_USER_PREFERENCES_KEY,
	CONSENT_BANNER_USER_PREFERENCES_SSR,
} from './consentBannerUserPreferencesKey.constants.tsx';

export type JiraPrefetchedConsentPreferenceData = PrefetchedConsentPreferenceData & {
	shouldShowBanner?: boolean;
};

const consentBannerUserPreferencesResource = createResource<JiraPrefetchedConsentPreferenceData>({
	type: CONSENT_BANNER_USER_PREFERENCES_SSR,
	getKey: () => CONSENT_BANNER_USER_PREFERENCES_KEY,
	getData: async () => {
		const consentToken = getCookie(ATLASSIAN_CONSENT_COOKIE_KEY, true);
		const fallbackConsentToken = getCookie(ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY, true);
		const shouldShowBannerBit = getCookie(ATLASSIAN_SHOW_BANNER_COOKIE_KEY, true);
		const shouldShowBanner =
			shouldShowBannerBit !== undefined && shouldShowBannerBit !== null
				? !!Number(shouldShowBannerBit)
				: undefined;
		// If we have cached cookie data, bundle it up, with a potential fallback token
		if (consentToken) {
			return { consentToken, fallbackConsentToken, shouldShowBanner };
		}
		if (!isFeatureEnabled('consent-platform', 'consenthub')) {
			return {};
		}
		/**
		 * On server we need to request the api using a server to server api (atl-preferences-center-elb)
		 * otherwise the request will not be authenticated correctly.
		 *
		 * However Pillar does not support ASAP, so we need to use the client side api call in local development.
		 *
		 * See https://atlassian.slack.com/archives/C017Y3EPAQ4/p1754626460768439?thread_ts=1754601759.549879&cid=C017Y3EPAQ4
		 */
		if (__SERVER__ && !getPillarLocalDevelopment()) {
			const userId = getAtlassianAccountId();
			if (userId) {
				const data = await performGetRequest<ConsentHubResponse>(
					`/rest/user/${userId}/consents/cookies`,
					{
						credentials: 'include',
						headers: {
							'x-ssr-service-name': 'atl-preferences-center',
						},
					},
				);
				return data;
			}
		}
		const data = await performGetRequest<ConsentHubResponse>(
			'/gateway/api/consenthub/session/user/consents/cookies',
		);
		return data;
	},
	maxAge: 15 * 60 * 1000,
	isBrowserOnly: false,
	isCritical: true,
});

export const getConsentBannerUserPreferencesResource =
	(): RouteResource<PrefetchedConsentPreferenceData> => consentBannerUserPreferencesResource;

export const useConsentBannerUserPreferencesResource =
	(): UseResourceHookResponse<PrefetchedConsentPreferenceData> =>
		useResource(getConsentBannerUserPreferencesResource());
